import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import axios from 'axios';
import {
    AUTH_STORAGE_EVENT,
    clearAuthStorage,
    getAccessToken,
    getStoredUser,
    isAccessTokenExpired,
    setAccessToken,
    setAuthStorage,
    setStoredUser,
    StoredUser,
} from './authStorage';

interface User extends StoredUser {
}

interface LoginResponse {
    email: string;
    name: string;
    picture: string | null;
    accessToken: string;
}

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    login: (data: LoginResponse) => void;
    updateUser: (user: User) => void;
    logout: () => void;
    loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const removeExpiredAccessTokenOnly = () => {
    if (isAccessTokenExpired(getAccessToken())) {
        localStorage.removeItem('accessToken');
    }
};

export const AuthProvider = ({children}: { children: ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;

        const syncUserFromStorage = () => {
            setUser(getStoredUser());
        };

        const restoreSession = async () => {
            const storedToken = getAccessToken();
            const storedUser = getStoredUser();

            if (!storedUser) {
                clearAuthStorage();
                if (isMounted) setUser(null);
                return;
            }

            if (storedToken && !isAccessTokenExpired(storedToken)) {
                if (isMounted) setUser(storedUser);
                return;
            }

            removeExpiredAccessTokenOnly();

            try {
                const refreshResponse = await axios.post(
                    `${process.env.REACT_APP_API_BASE_URL}/api/v1/auth/refresh`,
                    {},
                    {withCredentials: true}
                );

                setAccessToken(refreshResponse.data.accessToken);
                if (isMounted) setUser(storedUser);
            } catch {
                clearAuthStorage();
                if (isMounted) setUser(null);
            }
        };

        restoreSession().finally(() => {
            if (isMounted) setLoading(false);
        });

        window.addEventListener(AUTH_STORAGE_EVENT, syncUserFromStorage);
        window.addEventListener('storage', syncUserFromStorage);

        return () => {
            isMounted = false;
            window.removeEventListener(AUTH_STORAGE_EVENT, syncUserFromStorage);
            window.removeEventListener('storage', syncUserFromStorage);
        };
    }, []);

    const login = (data: LoginResponse) => {
        const {accessToken, ...userData} = data;

        setAuthStorage(accessToken, userData);
        setUser(userData);
    };

    const updateUser = (user: User) => {
        setStoredUser(user);
        setUser(user);
    };

    const logout = () => {
        clearAuthStorage();
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{
            user,
            isAuthenticated: !!user,
            login,
            updateUser,
            logout,
            loading
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
